/**
 * React Native `MicPermissionPort`.
 *
 * Core's session store drives this on audio start: `isGranted()` first, then
 * `request()`, aborting the call on denial — identical to web. What differs is
 * how the answer is obtained, and the two platforms genuinely differ:
 *
 *  - Android exposes a real permission API, so the state can be READ without
 *    prompting. That matters: `isGranted()` must never show a dialog, or the
 *    user gets prompted before they have pressed anything.
 *  - iOS has no readable pre-check from JS. The only probe is `getUserMedia`,
 *    which prompts on first use — the same fallback the web port uses for
 *    browsers without the Permissions API. Once answered, iOS resolves the
 *    probe from its own record without prompting again.
 *
 * The probe must never keep the microphone: every track it opens is stopped
 * before returning, or the OS recording indicator stays lit before the call
 * has even started.
 */

import { PermissionsAndroid, Platform } from "react-native";
import { mediaDevices } from "@livekit/react-native-webrtc";
import type { MicPermissionPort } from "@ringg/core";

/** Minimal shape of what `getUserMedia` hands back — enough to release it. */
interface ProbeStream {
  getTracks(): { stop(): void }[];
}

const stopTracks = (stream: ProbeStream): void => {
  stream.getTracks().forEach((track) => track.stop());
};

/** Opens and immediately releases an audio track. True when it was allowed. */
const probeMicrophone = async (): Promise<boolean> => {
  try {
    const stream = (await mediaDevices.getUserMedia({ audio: true })) as unknown as ProbeStream;
    stopTracks(stream);
    return true;
  } catch {
    return false;
  }
};

/**
 * Android's rationale copy. Shown only when the user has denied once before —
 * Android decides, not us.
 */
const ANDROID_RATIONALE = {
  title: "Microphone access",
  message: "Voice calls need the microphone to hear you.",
  buttonPositive: "Allow",
  buttonNegative: "Not now",
} as const;

export const createNativeMicPermission = (): MicPermissionPort => ({
  async isGranted(): Promise<boolean> {
    if (Platform.OS === "android") {
      // `check` is a pure read — no dialog, which is the whole point.
      return PermissionsAndroid.check(PermissionsAndroid.PERMISSIONS.RECORD_AUDIO);
    }
    // iOS/others: no readable state, so this doubles as the request. Core
    // calls `request()` right after a false, which is then a cheap no-op.
    return probeMicrophone();
  },

  async request(): Promise<boolean> {
    if (Platform.OS === "android") {
      const result = await PermissionsAndroid.request(PermissionsAndroid.PERMISSIONS.RECORD_AUDIO, ANDROID_RATIONALE);
      // `never_ask_again` is a denial as far as this call goes; core surfaces
      // the start-screen error and the user has to fix it in Settings.
      return result === PermissionsAndroid.RESULTS.GRANTED;
    }
    return probeMicrophone();
  },
});
